import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Shield, Sliders } from "lucide-react";

function buildQuery({ name, industry, postcode }) {
  const p = new URLSearchParams();
  if (name) p.set("q", name);
  if (industry) p.set("industry", industry);
  if (postcode) p.set("postcode", postcode);
  return p.toString();
}

export default function HeroSearch({
  inline = false,
  defaultName = "",
  defaultIndustry = "",
  defaultPostcode = "",
}) {
  const [name, setName] = useState(defaultName);
  const [industry, setIndustry] = useState(defaultIndustry);
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [showRefine, setShowRefine] = useState(Boolean(defaultIndustry || defaultPostcode));
  const nav = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = name.trim();
    if (q.length < 2) return;
    nav(`/check?${buildQuery({ name: q, industry: industry.trim(), postcode })}`);
  };

  const RefineFields = (
    <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
      <div>
        <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Sector / Industry</label>
        <input
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. hairdresser, plumber, accountant"
          maxLength={80}
          className="bnp-input"
          data-testid="hero-industry-input"
        />
      </div>
      <div>
        <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">UK postcode (optional)</label>
        <input
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="e.g. SW1A or BS1 5AB"
          maxLength={10}
          className="bnp-input"
          data-testid="hero-postcode-input"
        />
      </div>
    </div>
  );

  if (inline) {
    return (
      <form onSubmit={handleSubmit} className="w-full" data-testid="hero-search-form-inline">
        <div className="flex flex-col sm:flex-row sm:items-stretch bg-white rounded-md p-2 border border-[#9ac7f7]/40 bnp-soft-shadow gap-2 sm:gap-0">
          <div className="flex items-center gap-2 flex-1 sm:pl-3">
            <Search size={18} className="text-[#447694] shrink-0 ml-2 sm:ml-0" />
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type your business name"
              className="flex-1 bg-transparent outline-none text-[#04273d] placeholder:text-[#447694]/60 text-base py-1"
              data-testid="hero-search-input-inline"
            />
          </div>
          <button type="submit" className="btn-primary text-sm w-full sm:w-auto justify-center shrink-0" data-testid="hero-search-submit-inline">
            Check <ArrowRight size={14} />
          </button>
        </div>
        <div className="flex items-center justify-between mt-2 px-2">
          <button
            type="button"
            onClick={() => setShowRefine((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-display font-semibold text-[#04273d] bg-[#71edc9]/30 hover:bg-[#71edc9]/50 px-4 py-2 rounded-md transition-colors"
            data-testid="hero-refine-toggle-inline"
          >
            <Sliders size={14} />
            {showRefine ? "Hide industry & postcode" : "Add industry & postcode for sharper results"}
          </button>
        </div>
        {showRefine && RefineFields}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto" data-testid="hero-search-form">
      {/* Mobile: stacked input + full-width Check button underneath (easy thumb target).
          Desktop: classic side-by-side. */}
      <div className="flex flex-col sm:flex-row sm:items-stretch bg-white rounded-md p-2 border border-[#9ac7f7]/40 bnp-hero-shadow gap-2 sm:gap-0">
        <div className="flex items-center gap-2 flex-1 sm:pl-3">
          <Search size={22} className="text-[#447694] shrink-0 ml-3 sm:ml-2" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your business name e.g. Crown Hair Studio"
            className="flex-1 bg-transparent outline-none text-[#04273d] placeholder:text-[#447694]/55 text-base md:text-lg lg:text-xl py-3 pr-3"
            data-testid="hero-search-input"
          />
        </div>
        <button
          type="submit"
          className="btn-primary text-base px-6 md:px-8 py-4 bnp-pulse w-full sm:w-auto justify-center"
          data-testid="hero-search-submit"
        >
          <Shield size={18} />
          <span>Check name</span>
        </button>
      </div>

      <div className="flex items-center justify-center mt-3">
        <button
          type="button"
          onClick={() => setShowRefine((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-display font-semibold text-[#04273d] bg-[#71edc9]/30 hover:bg-[#71edc9]/50 px-5 py-2.5 rounded-md transition-colors"
          data-testid="hero-refine-toggle"
        >
          <Sliders size={14} />
          {showRefine ? "Hide industry & postcode" : "Add industry & postcode for sharper results"}
        </button>
      </div>

      {showRefine && (
        <div className="max-w-2xl mx-auto">
          {RefineFields}
        </div>
      )}
    </form>
  );
}
